import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import type { PlanEvent, Task } from "../../../../shared/types";
import type { HolidayEntry } from "../../../../shared/hooks/use-holidays";
import type { DayHeaderAction } from "../../types/desktop-calendar-view";
import { ContextMenu } from "../../../../ui/atoms/ContextMenu";
import {
  getDayName,
  getDayNumber,
} from "../../../../shared/types/date-helpers";
import { LockClosedIcon, LockOpenIcon } from "../../../../ui/icons";
import { TIME_COL_W } from "../../utils/time-constants";

const IS_MAC = typeof navigator !== "undefined" && /Mac/i.test(navigator.userAgent);
const MOD_KEY = IS_MAC ? "⌘" : "Ctrl";

export function CalendarHelpButton() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

  const handleToggle = useCallback(() => {
    setOpen((v) => {
      if (!v && btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + 6, left: r.left });
      }
      return !v;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      if (btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className={`w-5 h-5 flex items-center justify-center rounded text-[10px] font-bold transition-colors focus:outline-none ${
          open
            ? "text-accent bg-surface-alt"
            : "text-ink-faint/40 hover:text-ink-muted hover:bg-surface-alt active:bg-surface-alt/70"
        }`}
        title="Aide du calendrier"
        aria-label="Aide du calendrier"
      >
        ?
      </button>
      {open && pos && createPortal(
        <div
          ref={panelRef}
          className={`fixed z-[100] bg-surface border border-border rounded-xl shadow-xl animate-scale-in overflow-hidden ${
            isMobile ? "inset-x-4 top-16 w-auto" : "w-80"
          }`}
          style={isMobile ? undefined : { top: pos.top, left: pos.left }}
        >
          <div className="px-4 py-2.5 border-b border-border/30 flex items-center justify-between">
            <h3 className="text-xs font-bold text-ink">Raccourcis et fonctionnalités</h3>
            <button type="button" onClick={() => setOpen(false)} className="text-ink-faint hover:text-ink text-xs">✕</button>
          </div>
          <div className="px-4 py-3 flex flex-col gap-3 max-h-80 overflow-y-auto scrollbar-custom">

            {isMobile ? (
              <>
                <HelpSection title="Événements">
                  <HelpItem keys="Tap" desc="Créer un événement ou ouvrir un existant" />
                  <HelpItem keys="Appui long" desc="Menu : modifier, copier, supprimer" />
                  <HelpItem keys="Swipe" desc="Changer de jour (gauche/droite)" />
                </HelpSection>

                <HelpSection title="Tâches du jour">
                  <HelpItem keys="Tap" desc="Ouvrir le détail (priorité, sous-tâches)" />
                  <HelpItem keys="Statut" desc="Taper le statut pour le changer" />
                  <HelpItem keys="▾" desc="Replier / déplier les tâches" />
                  <HelpItem keys="Couleurs" desc="Rouge = urgent, gris = normal, clair = basse" />
                </HelpSection>

                <HelpSection title="Navigation">
                  <HelpItem keys="< >" desc="Jour précédent / suivant (mode Jour)" />
                  <HelpItem keys="Aujourd'hui" desc="Revenir au jour actuel" />
                  <HelpItem keys="Maintenant" desc="Retour auto à l'heure actuelle (30s)" />
                  <HelpItem keys={<span className="flex items-center gap-0.5 text-accent"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6" /></svg><svg className="w-3 h-3 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6" /></svg></span>} desc="Replier / déplier la barre de navigation" />
                </HelpSection>
              </>
            ) : (
              <>
                <HelpSection title="Événements">
                  <HelpItem keys="Clic" desc="Créer un événement (1h) ou ouvrir un existant" />
                  <HelpItem keys="Glisser" desc="Dessiner un créneau avec durée personnalisée" />
                  <HelpItem keys="Déplacer" desc="Glisser un événement vers un autre jour ou horaire" />
                  <HelpItem keys="Bords" desc="Tirer les bords pour redimensionner (inversible)" />
                  <HelpItem keys="Clic droit" desc="Modifier, dupliquer ou supprimer" />
                </HelpSection>

                <HelpSection title={`${MOD_KEY} et Shift`}>
                  <HelpItem keys={`${MOD_KEY} + clic`} desc="Créer par-dessus un événement (snap 15 min)" />
                  <HelpItem keys="Shift + clic" desc="Créer par-dessus un événement (snap 1 min)" />
                  <HelpItem keys="Shift + glisser" desc="Déplacer ou redimensionner à la minute" />
                  <HelpItem keys="Shift (survol)" desc="Indicateur d'heure par-dessus les événements" />
                </HelpSection>

                <HelpSection title="Tâches du jour">
                  <HelpItem keys="Clic" desc="Ouvrir le détail (priorité, sous-tâches, notes)" />
                  <HelpItem keys="Statut" desc="Cliquer sur le statut pour le changer rapidement" />
                  <HelpItem keys="Clic droit" desc="Changer statut, priorité ou supprimer" />
                  <HelpItem keys="Couleurs" desc="Rouge = urgent, gris = normal, clair = basse" />
                </HelpSection>

                <HelpSection title="Boutons">
                  <HelpItem keys={<LockClosedIcon className="w-3 h-3 inline" />} desc="Lecture seule (verrouiller le calendrier)" />
                  <HelpItem keys={<svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m7-5h4m0 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m7 5h4m0 0v-4m0 4l-5-5" /></svg>} desc="Plein écran" />
                  <HelpItem keys={<svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 12l2 2 4-4" /></svg>} desc="Afficher / masquer les tâches du jour" />
                </HelpSection>

                <HelpSection title="Navigation">
                  <HelpItem keys="Clic jour" desc="Menu : ajouter événement, tâche ou voir le jour" />
                  <HelpItem keys="< >" desc="Semaine précédente / suivante" />
                  <HelpItem keys="Aujourd'hui" desc="Revenir au jour actuel" />
                  <HelpItem keys="Maintenant" desc="Retour auto à l'heure actuelle (30s)" />
                </HelpSection>
              </>
            )}

          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function HelpSection({ title, children }: { readonly title: string; readonly children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] text-ink-faint uppercase tracking-wide font-semibold mb-1.5">{title}</p>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 items-start">{children}</div>
    </div>
  );
}

function HelpItem({ keys, desc }: { readonly keys: React.ReactNode; readonly desc: string }) {
  return (
    <>
      {typeof keys === "string" ? (
        <kbd className="text-[9px] font-mono bg-surface-alt rounded px-1.5 py-0.5 text-ink-muted text-center whitespace-nowrap">{keys}</kbd>
      ) : (
        <span className="inline-flex items-center justify-center bg-surface-alt rounded px-1.5 py-0.5 text-ink-muted">{keys}</span>
      )}
      <span className="text-[11px] text-ink-muted leading-tight">{desc}</span>
    </>
  );
}

interface WeekGridDayHeadersProps {
  readonly days: readonly string[];
  readonly today: string;
  readonly selectedDate?: string | null;
  readonly isExpanded?: boolean;
  readonly readOnly?: boolean;
  readonly onToggleExpand?: () => void;
  readonly onToggleReadOnly?: () => void;
  readonly eventsByDate?: ReadonlyMap<string, PlanEvent[]>;
  readonly tasksByDate?: ReadonlyMap<string, Task[]>;
  readonly onDayAction?: (day: string, action: DayHeaderAction) => void;
  readonly hasTasks?: boolean;
  readonly tasksCollapsed?: boolean;
  readonly onToggleTasksCollapsed?: () => void;
  readonly doneTasks?: number;
  readonly totalTasks?: number;
  readonly holidays?: ReadonlyMap<string, HolidayEntry>;
}

interface MenuState {
  readonly day: string;
  readonly anchorRect: DOMRect;
}

export function WeekGridDayHeaders({
  days,
  today,
  selectedDate,
  isExpanded,
  readOnly,
  onToggleExpand,
  onToggleReadOnly,
  eventsByDate,
  onDayAction,
  hasTasks,
  tasksCollapsed,
  onToggleTasksCollapsed,
  doneTasks = 0,
  totalTasks = 0,
  holidays,
}: WeekGridDayHeadersProps) {
  const [menuState, setMenuState] = useState<MenuState | null>(null);

  const openMenu = useCallback((day: string, target: HTMLElement) => {
    setMenuState({ day, anchorRect: target.getBoundingClientRect() });
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent, day: string) => {
      if (!onDayAction) return;
      e.preventDefault();
      openMenu(day, e.currentTarget as HTMLElement);
    },
    [onDayAction, openMenu],
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, day: string) => {
      if (!onDayAction) return;
      e.preventDefault();
      openMenu(day, e.currentTarget as HTMLElement);
    },
    [onDayAction, openMenu],
  );

  const buildMenuItems = useCallback(
    (day: string) => {
      const dayEvents = eventsByDate?.get(day) ?? [];
      const hasEvents = dayEvents.length > 0;

      const items = [
        {
          label: "Ajouter un événement",
          onClick: () => onDayAction?.(day, "addEvent"),
        },
        {
          label: "Ajouter une tâche",
          onClick: () => onDayAction?.(day, "addTask"),
        },
        {
          label: "Voir le jour",
          onClick: () => onDayAction?.(day, "navigateToDay"),
        },
      ];

      if (hasEvents) {
        items.push({
          label: "Supprimer les événements du jour",
          onClick: () => onDayAction?.(day, "deleteDayEvents"),
          danger: true,
          separator: true,
        } as (typeof items)[0]);
      }

      return items;
    },
    [eventsByDate, onDayAction],
  );

  return (
    <div
      className="sticky top-0 z-20 grid bg-surface border-b border-border/15"
      style={{
        gridTemplateColumns: `${TIME_COL_W}px repeat(${days.length}, 1fr)`,
      }}
    >
      <div className="border-r border-border/10 flex flex-col items-center justify-center gap-1">
        <div className="flex items-center gap-0.5">
          {onToggleReadOnly && !isExpanded && (
            <button
              type="button"
              onClick={onToggleReadOnly}
              aria-label={readOnly ? "Déverrouiller le calendrier" : "Verrouiller le calendrier"}
            title={readOnly ? "Déverrouiller le calendrier" : "Verrouiller le calendrier"}
              className={`w-6 h-6 flex items-center justify-center rounded transition-colors focus:outline-none ${
                readOnly
                  ? "text-accent hover:text-accent/70"
                  : "text-ink-faint/50 hover:text-ink-muted hover:bg-surface-alt active:bg-surface-alt/70"
              }`}
            >
              {readOnly ? (
                <LockClosedIcon className="w-3.5 h-3.5" />
              ) : (
                <LockOpenIcon className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          {onToggleExpand && (
            <button
              type="button"
              onClick={onToggleExpand}
              aria-label={isExpanded ? "Réduire le calendrier" : "Agrandir le calendrier"}
            title={isExpanded ? "Réduire le calendrier" : "Agrandir le calendrier"}
              className="w-6 h-6 flex items-center justify-center rounded text-ink-faint/50 hover:text-ink-muted hover:bg-surface-alt active:bg-surface-alt/70 transition-colors focus:outline-none"
            >
              {isExpanded ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9L4 4m0 0h5m-5 0v5M15 9l5-5m0 0h-5m5 0v5M9 15l-5 5m0 0h5m-5 0v-5M15 15l5 5m0 0h-5m5 0v-5" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m7-5h4m0 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m7 5h4m0 0v-4m0 4l-5-5" />
                </svg>
              )}
            </button>
          )}
        </div>
        <CalendarHelpButton />
        {hasTasks && onToggleTasksCollapsed && (
          <button
            type="button"
            onClick={onToggleTasksCollapsed}
            aria-label={tasksCollapsed ? "Afficher les tâches du jour" : "Masquer les tâches du jour"}
            className={`w-6 h-6 flex items-center justify-center rounded transition-colors focus:outline-none ${
              !tasksCollapsed
                ? "text-accent hover:text-accent/70 hover:bg-surface-alt active:bg-surface-alt/70"
                : "text-ink-faint/50 hover:text-ink-muted hover:bg-surface-alt active:bg-surface-alt/70"
            }`}
            title={`Tâches du jour · ${doneTasks}/${totalTasks}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </button>
        )}
      </div>
      {days.map((day) => {
        const isToday = day === today;
        const isSelected = !isToday && day === selectedDate;
        return (
          <div
            key={day}
            className="flex flex-col items-center gap-0.5 py-2 border-l border-border/10 cursor-pointer select-none hover:bg-surface-alt/40 active:bg-surface-alt/60 transition-colors"
            onClick={(e) => handleClick(e, day)}
            onContextMenu={(e) => handleContextMenu(e, day)}
          >
            <span className={`text-[11px] font-medium uppercase tracking-wide ${isToday || isSelected ? "text-accent" : "text-ink-faint"}`}>
              {getDayName(day)}
            </span>
            <span
              className={`text-2xl font-light leading-none w-10 h-10 flex items-center justify-center rounded-full ${
                isToday
                  ? "bg-accent text-white"
                  : isSelected
                    ? "ring-2 ring-accent text-accent"
                    : "text-ink hover:bg-surface-alt active:bg-surface-alt/70"
              }`}
            >
              {getDayNumber(day)}
            </span>
            {holidays?.get(day) && (
              <span
                className={`text-[8px] font-medium truncate max-w-full px-0.5 leading-tight ${holidays.get(day)!.official ? "text-red-500 dark:text-red-400" : "text-ink-faint"}`}
                title={holidays.get(day)!.official ? `${holidays.get(day)!.name} — Jour férié` : holidays.get(day)!.name}
              >
                {holidays.get(day)!.name}
              </span>
            )}
          </div>
        );
      })}

      {menuState && (
        <ContextMenu
          items={buildMenuItems(menuState.day)}
          anchorRect={menuState.anchorRect}
          onClose={() => setMenuState(null)}
        />
      )}
    </div>
  );
}
